// Cloud speech playback — her voice on devices whose browser has no speech
// engine at all (Android WebViews, several in-app browsers). Audio arrives as
// SSE-wrapped 24kHz PCM and is scheduled chunk by chunk on an AudioContext, so
// she starts talking while the rest is still being generated.

import { getVoiceA11y, publishCaption } from "./voiceA11y";
import { stripMarkdownForSpeech } from "./ttsText";
import { tapVoiceBus } from "./voiceLevel";
import { routedDestination, wakeOutput, type RoutedOutput } from "./audioOut";
import { registerAudioContext } from "./audioUnlock";

let ctx: AudioContext | null = null;
let abort: AbortController | null = null;
let live = 0;              // chunks scheduled but not finished
let generating = false;
const sources = new Set<AudioBufferSourceNode>();
let activeGain: GainNode | null = null;
let routed: RoutedOutput | null = null;

/** Her voice is generated as 24kHz mono PCM whatever the device runs at. */
const RATE = 24000;

// --- why nothing was heard -------------------------------------------------
// Silence with no explanation is the whole complaint about chat playback, so
// every reason we can detect is broadcast instead of being swallowed.
const failWatchers = new Set<(reason: string) => void>();

export function subscribeCloudSpeechFailure(fn: (reason: string) => void): () => void {
  failWatchers.add(fn);
  return () => failWatchers.delete(fn);
}
function reportFailure(reason: string): void {
  failWatchers.forEach((fn) => { try { fn(reason); } catch { /* watcher noise */ } });
}

/** Cloud voice needs nothing but fetch + WebAudio. */
export function cloudSpeechAvailable(): boolean {
  return typeof window !== "undefined" &&
    typeof fetch === "function" &&
    !!(window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
}

export function cloudSpeaking(): boolean {
  return generating || live > 0;
}

export function stopCloudSpeech(): void {
  speechEpoch++;
  abort?.abort();
  abort = null;
  generating = false;

  // Ramp the bus down before killing sources — cutting a waveform mid-cycle
  // is exactly what makes a "pop".
  const c = ctx;
  const g = activeGain;
  const stopAt = c && g ? c.currentTime + 0.02 : 0;
  if (c && g) {
    try {
      g.gain.cancelScheduledValues(c.currentTime);
      g.gain.setValueAtTime(Math.max(0.0001, g.gain.value), c.currentTime);
      g.gain.exponentialRampToValueAtTime(0.0001, stopAt);
    } catch { /* ignore */ }
  }
  for (const s of sources) { try { s.stop(stopAt); } catch { try { s.stop(); } catch { /* ended */ } } }
  sources.clear();
  activeGain = null;
  const r = routed;
  routed = null;
  if (r) window.setTimeout(() => { try { r.dispose(); } catch { /* noop */ } }, 60);
  live = 0;
  publishCaption(null);
}


function audioCtx(): AudioContext {
  const Ctor = window.AudioContext ||
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  if (ctx && ctx.state !== "closed") return ctx;
  // Asking for a 24kHz context is a convenience, not a requirement: several
  // phones and every Safari before 15 refuse a rate the hardware does not run
  // at and throw, which used to abort playback with no sound and no message.
  // Falling back to the device's own rate is safe because each buffer declares
  // its own 24kHz rate and Web Audio resamples it on the way out.
  try {
    ctx = new Ctor({ sampleRate: RATE });
  } catch {
    ctx = new Ctor();
  }
  registerAudioContext(ctx);
  return ctx;
}


/**
 * Autoplay unlock. Her replies arrive asynchronously, so the AudioContext is
 * built and resumed *after* the tap that asked for them — and phones, WebViews
 * and sandboxed preview frames refuse that: the context stays suspended and
 * playback is silent with no error at all. Creating and resuming it during a
 * real gesture keeps it running for every later reply.
 */
export function primeCloudAudio(): void {
  if (!cloudSpeechAvailable()) return;
  try {
    const c = audioCtx();
    if (c.state === "suspended") void c.resume().catch(() => {});
  } catch { /* audio unavailable on this device */ }
}

if (typeof window !== "undefined") {
  const unlock = () => primeCloudAudio();
  for (const ev of ["pointerdown", "touchstart", "keydown"] as const) {
    try { window.addEventListener(ev, unlock, { capture: true, passive: true }); } catch { /* ignore */ }
  }
}


/**
 * Split a long reply into passages the voice engine can hold in memory at
 * once, cutting on sentence ends so no word is ever halved.
 */
function splitPassages(text: string, max: number): string[] {
  if (text.length <= max) return [text];
  const out: string[] = [];
  let buf = "";
  for (const sentence of text.split(/(?<=[.!?…])\s+/)) {
    const s = sentence.trim();
    if (!s) continue;
    if (s.length > max) {
      if (buf) { out.push(buf); buf = ""; }
      let rest = s;
      while (rest.length > max) {
        const space = rest.lastIndexOf(" ", max);
        const cut = space > max * 0.5 ? space : max;
        out.push(rest.slice(0, cut).trim());
        rest = rest.slice(cut).trim();
      }
      buf = rest;
      continue;
    }
    if ((buf ? `${buf} ${s}` : s).length <= max) { buf = buf ? `${buf} ${s}` : s; continue; }
    out.push(buf);
    buf = s;
  }
  if (buf) out.push(buf);
  return out.filter(Boolean);
}

/** One passage per generation — bumped whenever playback is stopped. */
let speechEpoch = 0;

/**
 * Speak a whole reply through the cloud voice, however long it runs.
 * Her forensic answers can be tens of thousands of characters, and streaming
 * that as one PCM file exhausted memory — so the reply is read as a sequence
 * of passages, each one starting as the previous finishes. Nothing is dropped.
 */
export async function cloudSpeak(text: string, onEnd?: () => void): Promise<void> {
  if (!cloudSpeechAvailable()) { onEnd?.(); return; }
  const clean = stripMarkdownForSpeech(text).trim();
  if (!clean) { onEnd?.(); return; }
  stopCloudSpeech();
  const epoch = ++speechEpoch;
  const passages = splitPassages(clean, 2600);
  for (const passage of passages) {
    if (epoch !== speechEpoch) return;      // someone else took the voice
    await new Promise<void>((resolve) => {
      let done = false;
      void speakPassage(passage, () => { if (!done) { done = true; resolve(); } });
    });
  }
  if (epoch === speechEpoch) onEnd?.();
}

/**
 * Speak one passage. Resolves when playback finishes.
 * Must be kicked off from a user gesture the first time so the AudioContext
 * is allowed to start.
 */
async function speakPassage(clean: string, onEnd?: () => void): Promise<void> {
  // Release the previous passage's output nodes without touching the epoch —
  // stopCloudSpeech() would read as "someone cancelled her" mid-reply.
  const stale = routed;
  routed = null;
  activeGain = null;
  if (stale) window.setTimeout(() => { try { stale.dispose(); } catch { /* noop */ } }, 60);



  // Claim "she is talking" before the first await: the play button polls this,
  // and during the resume/route/fetch gap it used to read "silent" and reset
  // itself while her line was still on the way.
  generating = true;
  let c: AudioContext;
  try {
    c = audioCtx();
  } catch {
    generating = false;
    reportFailure("This device would not open an audio output for her voice.");
    onEnd?.();
    return;
  }
  if (c.state === "suspended") {
    try { await c.resume(); } catch { /* blocked */ }
    // Still blocked: the next real touch or key press starts the output, so the
    // audio already scheduled below becomes audible instead of vanishing.
    if (c.state === "suspended") {
      reportFailure("Tap the screen once — this browser holds her voice back until you do.");
      const kick = () => { void c.resume().catch(() => {}); };
      for (const ev of ["pointerdown", "touchstart", "keydown"] as const) {
        try { window.addEventListener(ev, kick, { once: true, capture: true, passive: true }); } catch { /* ignore */ }
      }
    }
  }


  const gain = c.createGain();
  const a11y = getVoiceA11y();
  gain.gain.value = 0.0001;
  // Speak out of whichever output you chose — her chat voice used to be pinned
  // to the system default, so with a Bluetooth headset selected the reply went
  // to a speaker nobody was listening to.
  let out: AudioNode = c.destination;
  try {
    routed = await routedDestination(c);
    out = routed.node;
    if (routed.via !== "default") {
      // A2DP sleeps between sounds and swallows the first syllable.
      await wakeOutput(c, out);
    }
  } catch { out = c.destination; }
  gain.connect(out);
  activeGain = gain;
  // Let the UI see her waveform. A tap only — never in the path to the speakers.
  tapVoiceBus(c, gain);


  const controller = new AbortController();
  abort = controller;
  generating = true;
  publishCaption(clean);

  let pending = new Uint8Array(0);

  const finish = () => {
    if (generating || live > 0) return;
    publishCaption(null);
    onEnd?.();
  };

  // Pop control. Two causes of clicks with streamed PCM:
  //  1. float accumulation on the playhead leaves sub-sample gaps between
  //     buffers, so we count *frames* instead and derive each start time.
  //  2. an abrupt jump from silence to full amplitude at the very start (and
  //     on stop), so the output gain ramps over a few milliseconds.
  const SEGMENT = 0.5;                        // seconds of audio per buffer
  const SEGMENT_BYTES = Math.floor(SEGMENT * RATE) * 2;
  const PREROLL = 0.5;                        // seconds of cushion
  const FADE = 0.012;                         // start/stop ramp
  const speed = Math.min(2, Math.max(0.5, a11y.speed));
  const target = a11y.volume > 0 ? a11y.volume : 1;
  gain.gain.value = 0.0001;



  let baseTime = 0;      // context time the first sample plays at
  let framesOut = 0;     // frames already scheduled

  const schedule = (pcm: Uint8Array) => {
    const frames = Math.floor(pcm.length / 2);
    if (frames === 0) return;
    // Copy into an aligned buffer — SSE slices are not guaranteed 2-byte aligned.
    const aligned = new Uint8Array(frames * 2);
    aligned.set(pcm.subarray(0, frames * 2));
    const samples = new Int16Array(aligned.buffer);
    const floats = Float32Array.from(samples, (s) => s / 32768);
    const buffer = c.createBuffer(1, floats.length, RATE);
    buffer.copyToChannel(floats, 0);
    const src = c.createBufferSource();
    src.buffer = buffer;
    src.playbackRate.value = speed;
    src.connect(gain);

    const now = c.currentTime;
    let startAt = baseTime + framesOut / (RATE * speed);
    if (baseTime === 0 || startAt < now) {
      // first segment, or the network fell behind — re-cushion and fade in
      // rather than butt a buffer against a gap of silence.
      baseTime = now + PREROLL;
      framesOut = 0;
      startAt = baseTime;
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(Math.max(0.0001, gain.gain.value), now);
      gain.gain.exponentialRampToValueAtTime(target, baseTime + FADE);
    }
    live += 1;
    sources.add(src);
    src.onended = () => { sources.delete(src); live = Math.max(0, live - 1); finish(); };
    src.start(startAt);
    framesOut += frames;
  };

  const playChunk = (incoming: Uint8Array, flush = false) => {
    const bytes = new Uint8Array(pending.length + incoming.length);
    bytes.set(pending);
    bytes.set(incoming, pending.length);
    pending = bytes;
    while (pending.length >= SEGMENT_BYTES) {
      schedule(pending.subarray(0, SEGMENT_BYTES));
      pending = pending.slice(SEGMENT_BYTES);
    }
    if (flush && pending.length >= 2) {
      // Taper the tail so the last sample doesn't cut off mid-waveform.
      // The end time must be read *after* scheduling: on a short reply this is
      // the first segment, and baseTime is still zero until schedule() sets it.
      schedule(pending);
      pending = new Uint8Array(0);
      const endAt = baseTime + framesOut / (RATE * speed);
      try {
        gain.gain.setValueAtTime(target, Math.max(c.currentTime, endAt - FADE));
        gain.gain.exponentialRampToValueAtTime(0.0001, Math.max(c.currentTime + FADE, endAt));
      } catch { /* ramp outside range */ }
    }

  };



  try {
    const res = await fetch("/api/tts", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: clean }),
      signal: controller.signal,
    });
    if (!res.ok || !res.body) throw new Error(`voice ${res.status}`);

    const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
    let buf = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += value;
      const lines = buf.split("\n");
      buf = lines.pop() ?? "";
      for (const line of lines) {
        if (!line.startsWith("data:")) continue;
        const payloadRaw = line.slice(5).trim();
        if (!payloadRaw || payloadRaw === "[DONE]") continue;
        let payload: { type?: string; audio?: string };
        try { payload = JSON.parse(payloadRaw); } catch { continue; }
        if (payload.type !== "speech.audio.delta" || !payload.audio) continue;
        const bin = atob(payload.audio);
        const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        playChunk(arr);
      }
    }
    playChunk(new Uint8Array(0), true);   // flush the final partial segment
  } catch (e) {
    // Offline or refused: say so instead of leaving a dead play button.
    if (!(e instanceof DOMException && e.name === "AbortError")) {
      reportFailure(navigator.onLine === false
        ? "No voice on this device, and her cloud voice needs a signal."
        : "Her voice could not be fetched just now.");
    }
  } finally {
    generating = false;
    if (abort === controller) abort = null;
    finish();
  }
}
